'use client';

import { c as _c } from "react/compiler-runtime";
import type { ComponentProps, HTMLAttributes, KeyboardEventHandler } from 'react';
import { Textarea } from '@repo/ui/components';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/components';
import { Button } from '@repo/ui/components';
import { cn } from '@repo/ui/lib/utils';
import { Loader2Icon, SendIcon, SquareIcon, XIcon } from 'lucide-react';
import { Children, useCallback, useEffect, useRef } from 'react';
type UseAutoResizeTextareaProps = {
  maxHeight?: number;
  minHeight: number;
};
const useAutoResizeTextarea = t0 => {
  const $ = _c(11);
  const {
    maxHeight,
    minHeight
  } = t0;
  const textareaRef = useRef(null);
  let t1;
  if ($[0] !== maxHeight || $[1] !== minHeight) {
    t1 = reset => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }
      if (reset) {
        textarea.style.height = `${minHeight}px`;
        return;
      }
      textarea.style.height = `${minHeight}px`;
      const newHeight = Math.max(minHeight, Math.min(textarea.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY));
      textarea.style.height = `${newHeight}px`;
    };
    $[0] = maxHeight;
    $[1] = minHeight;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const adjustHeight = t1;
  let t2;
  let t3;
  if ($[3] !== minHeight) {
    t2 = () => {
      const textarea_0 = textareaRef.current;
      if (textarea_0) {
        textarea_0.style.height = `${minHeight}px`;
      }
    };
    t3 = [minHeight];
    $[3] = minHeight;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[6] !== adjustHeight) {
    t4 = () => {
      const handleResize = () => adjustHeight();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    };
    t5 = [adjustHeight];
    $[6] = adjustHeight;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== adjustHeight) {
    t6 = {
      adjustHeight,
      textareaRef
    };
    $[9] = adjustHeight;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
export type AIInputProps = HTMLAttributes<HTMLFormElement>;
export const AIInput = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("w-full divide-y overflow-hidden rounded-xl border bg-background shadow-sm", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <form className={t1} {...props} data-sentry-component="AIInput" data-sentry-source-file="input.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type AIInputTextareaProps = ComponentProps<typeof Textarea> & {
  maxHeight?: number;
  minHeight?: number;
};
export const AIInputTextarea = t0 => {
  const $ = _c(21);
  let className;
  let onChange;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      className,
      maxHeight: t1,
      minHeight: t2,
      onChange,
      placeholder: t3,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onChange;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    className = $[1];
    onChange = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const maxHeight = t1 === undefined ? 164 : t1;
  const minHeight = t2 === undefined ? 48 : t2;
  const placeholder = t3 === undefined ? "What would you like to know?" : t3;
  let t4;
  if ($[7] !== maxHeight || $[8] !== minHeight) {
    t4 = {
      maxHeight,
      minHeight
    };
    $[7] = maxHeight;
    $[8] = minHeight;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const {
    adjustHeight,
    textareaRef
  } = useAutoResizeTextarea(t4);
  const handleKeyDown = _temp;
  let t5;
  if ($[10] !== className) {
    t5 = cn("w-full resize-none rounded-none border-none p-3 shadow-none ring-0 outline-none", "bg-transparent dark:bg-transparent", "focus-visible:ring-0", className);
    $[10] = className;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== adjustHeight || $[13] !== onChange) {
    t6 = e_0 => {
      adjustHeight();
      onChange?.(e_0);
    };
    $[12] = adjustHeight;
    $[13] = onChange;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== placeholder || $[16] !== props || $[17] !== t5 || $[18] !== t6 || $[19] !== textareaRef) {
    t7 = <Textarea className={t5} name="message" onChange={t6} onKeyDown={handleKeyDown} placeholder={placeholder} ref={textareaRef} {...props} data-sentry-element="Textarea" data-sentry-component="AIInputTextarea" data-sentry-source-file="input.tsx" />;
    $[15] = placeholder;
    $[16] = props;
    $[17] = t5;
    $[18] = t6;
    $[19] = textareaRef;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
export type AIInputToolbarProps = HTMLAttributes<HTMLDivElement>;
export const AIInputToolbar = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex items-center justify-between p-1", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} data-sentry-component="AIInputToolbar" data-sentry-source-file="input.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type AIInputToolsProps = HTMLAttributes<HTMLDivElement>;
export const AIInputTools = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("flex items-center gap-1", "[&_button:first-child]:rounded-bl-xl", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div className={t1} {...props} data-sentry-component="AIInputTools" data-sentry-source-file="input.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type AIInputButtonProps = ComponentProps<typeof Button>;
export const AIInputButton = t0 => {
  const $ = _c(14);
  let className;
  let props;
  let t1;
  let variant;
  if ($[0] !== t0) {
    const {
      className: t2,
      size,
      variant: t3,
      ...t4
    } = t0;
    className = t2;
    props = t4;
    variant = t3 === undefined ? "ghost" : t3;
    t1 = size ?? Children.count(props.children) > 1 ? "default" : "icon";
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
    $[4] = variant;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
    variant = $[4];
  }
  const newSize = t1;
  const t2 = variant === "ghost" && "text-muted-foreground";
  const t3 = newSize === "default" && "px-3";
  let t4;
  if ($[5] !== className || $[6] !== t2 || $[7] !== t3) {
    t4 = cn("shrink-0 gap-1.5 rounded-lg", t2, t3, className);
    $[5] = className;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== newSize || $[10] !== props || $[11] !== t4 || $[12] !== variant) {
    t5 = <Button className={t4} size={newSize} type="button" variant={variant} {...props} data-sentry-element="Button" data-sentry-component="AIInputButton" data-sentry-source-file="input.tsx" />;
    $[9] = newSize;
    $[10] = props;
    $[11] = t4;
    $[12] = variant;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
export type AIInputSubmitProps = ComponentProps<typeof Button> & {
  status?: 'error' | 'ready' | 'streaming' | 'submitted';
};
export const AIInputSubmit = t0 => {
  const $ = _c(19);
  let children;
  let className;
  let props;
  let status;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      className,
      size: t1,
      status,
      variant: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = status;
    $[5] = t1;
    $[6] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    status = $[4];
    t1 = $[5];
    t2 = $[6];
  }
  const size = t1 === undefined ? "icon" : t1;
  const variant = t2 === undefined ? "default" : t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SendIcon />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let Icon = t3;
  if (status === "submitted") {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Loader2Icon className="animate-spin" />;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    Icon = t4;
  } else {
    if (status === "streaming") {
      let t4;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = <SquareIcon />;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      Icon = t4;
    } else {
      if (status === "error") {
        let t4;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = <XIcon />;
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        Icon = t4;
      }
    }
  }
  let t4;
  if ($[11] !== className) {
    t4 = cn("gap-1.5 rounded-lg rounded-br-xl", className);
    $[11] = className;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = children ?? Icon;
  let t6;
  if ($[13] !== props || $[14] !== size || $[15] !== t4 || $[16] !== t5 || $[17] !== variant) {
    t6 = <Button className={t4} size={size} type="submit" variant={variant} {...props} data-sentry-element="Button" data-sentry-component="AIInputSubmit" data-sentry-source-file="input.tsx">{t5}</Button>;
    $[13] = props;
    $[14] = size;
    $[15] = t4;
    $[16] = t5;
    $[17] = variant;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
export type AIInputModelSelectProps = ComponentProps<typeof Select>;
export const AIInputModelSelect = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Select {...props} data-sentry-element="Select" data-sentry-component="AIInputModelSelect" data-sentry-source-file="input.tsx" />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export type AIInputModelSelectTriggerProps = ComponentProps<typeof SelectTrigger>;
export const AIInputModelSelectTrigger = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("border-none bg-transparent font-medium text-muted-foreground shadow-none transition-colors", "hover:bg-accent hover:text-foreground [&[aria-expanded=\"true\"]]:bg-accent [&[aria-expanded=\"true\"]]:text-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectTrigger className={t1} {...props} data-sentry-element="SelectTrigger" data-sentry-component="AIInputModelSelectTrigger" data-sentry-source-file="input.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type AIInputModelSelectContentProps = ComponentProps<typeof SelectContent>;
export const AIInputModelSelectContent = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectContent className={t1} {...props} data-sentry-element="SelectContent" data-sentry-component="AIInputModelSelectContent" data-sentry-source-file="input.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type AIInputModelSelectItemProps = ComponentProps<typeof SelectItem>;
export const AIInputModelSelectItem = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectItem className={t1} {...props} data-sentry-element="SelectItem" data-sentry-component="AIInputModelSelectItem" data-sentry-source-file="input.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
export type AIInputModelSelectValueProps = ComponentProps<typeof SelectValue>;
export const AIInputModelSelectValue = t0 => {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectValue className={t1} {...props} data-sentry-element="SelectValue" data-sentry-component="AIInputModelSelectValue" data-sentry-source-file="input.tsx" />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
function _temp(e) {
  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    const form = e.currentTarget.form;
    if (form) {
      form.requestSubmit();
    }
  }
}